'use client';

import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from 'motion/react';
import {
  createContext,
  useContext,
  useSyncExternalStore,
  type ReactNode,
  type RefObject,
} from 'react';
import { useMediaQuery } from '@/hooks/useMediaQuery.hook';
import { resolveStackMode, type StackMode } from '../_utils/stack-mode';

/*
 * 모바일 카드 스택 등장의 motion 대체 구동. 평소엔 stats.css 의 scroll-driven animation 이 돌고,
 * 그걸 못 쓸 때(미지원 브라우저·동작 줄이기)만 섹션 스크롤 진행률로 StackReveal 이 움직인다
 */

// stats.css 모바일 경계(max-width: 989.98px)·Tailwind lg(990px)와 같은 값
const MOBILE_QUERY = '(max-width: 989.98px)';

// 시안 스프링(stats.css --ty-spring 과 같은 곡선)
const spring = (t: number) =>
  1 -
  Math.exp(-7.6657 * t) *
    (Math.cos(6.7605 * t) + 1.1339 * Math.sin(6.7605 * t));

// entry = 섹션 윗변이 화면 아래 → 위(무대 고정 직전), pin = 무대 고정 구간
const PROGRESS_OFFSET = {
  entry: ['start end', 'start start'],
  pin: ['start start', 'end end'],
} as const;

// 구간 비율·상승 거리는 stats.css 와 같은 시안 실측
const STACK_ITEMS = {
  title: { progress: 'entry', range: [0.4, 1], risePx: 40 },
  server: { progress: 'pin', range: [0, 1 / 3], risePx: 220 },
  dna: { progress: 'pin', range: [1 / 3, 2 / 3], risePx: 260 },
  variants: { progress: 'pin', range: [2 / 3, 1], risePx: 300 },
} as const;

type StackItem = keyof typeof STACK_ITEMS;
type Section = RefObject<HTMLElement | null>;

type StackRevealState = { mode: StackMode; section: Section };

// 사이에 낀 서버 컴포넌트를 거치지 않고 모드와 섹션을 넘긴다. 기본값 = 연출 없음
const StackRevealContext = createContext<StackRevealState>({
  mode: null,
  section: { current: null },
});

export const StackRevealProvider = StackRevealContext.Provider;

const subscribeNever = () => () => {};

/** scroll-driven animation 지원 여부. 서버에선 지원으로 본다 — 하이드레이션 전엔 CSS 에 맡긴다 */
function useSupportsScrollTimeline() {
  return useSyncExternalStore(
    subscribeNever,
    () => CSS.supports('animation-timeline: view()'),
    () => true,
  );
}

/** 섹션 기준 구동 모드. 스크롤 구독은 모드가 켜진 StackReveal 만 한다 */
export function useStackReveal(section: Section): StackRevealState {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const reduced = useReducedMotion() ?? false;
  const hasScrollTimeline = useSupportsScrollTimeline();
  return {
    mode: resolveStackMode(isMobile, reduced, hasScrollTimeline),
    section,
  };
}

/** 모바일 스택에서 스크롤에 맞춰 떠오르는 한 칸. 모드가 없으면 stats.css 에 맡긴다 */
export function StackReveal({
  item,
  className,
  children,
}: {
  item: StackItem;
  className?: string;
  children: ReactNode;
}) {
  const { mode, section } = useContext(StackRevealContext);
  if (!mode) return <div className={className}>{children}</div>;
  return (
    <ScrollReveal
      item={item}
      mode={mode}
      section={section}
      className={className}
    >
      {children}
    </ScrollReveal>
  );
}

function ScrollReveal({
  item,
  mode,
  section,
  className,
  children,
}: {
  item: StackItem;
  mode: NonNullable<StackMode>;
  section: Section;
  className?: string;
  children: ReactNode;
}) {
  const { progress, range, risePx } = STACK_ITEMS[item];
  const { scrollYProgress } = useScroll({
    target: section,
    offset: [...PROGRESS_OFFSET[progress]],
  });
  const opacity = useTransform(scrollYProgress, [...range], [0, 1], {
    ease: spring,
  });
  const y = useTransform(scrollYProgress, [...range], [risePx, 0], {
    ease: spring,
  });

  return (
    <motion.div
      className={className}
      style={{ opacity, y: mode === 'fade' ? 0 : y }}
    >
      {children}
    </motion.div>
  );
}
